import React, { useState } from 'react';
import {
  useInstances,
  useTemplates,
  useAddChecklistFromTemplate,
  useChecklistItems,
  useTogglePacked,
  useReorderItems,
  useAddChecklistItem,
  useRemoveChecklistItem,
} from '../hooks/useTravelBuddy';
import { SortableChecklist } from './SortableChecklist';
import type { ChecklistInstance, ChecklistKind } from '../types';
import * as T from '../ui/inlineTheme';
import { sf } from '../../themeConfig/sisuForgeTokens';

const templateCard: React.CSSProperties = {
  ...T.packingCard,
  marginBottom: 0,
};

const emptyState: React.CSSProperties = {
  ...T.packingCard,
  borderStyle: 'dashed',
  textAlign: 'center',
  fontFamily: T.font,
  fontSize: 15,
  color: sf.textMuted,
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
};

const addRow: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  marginBottom: 16,
  alignItems: 'stretch',
};

const addInput: React.CSSProperties = {
  ...T.input,
  flex: 1,
  minHeight: 46,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surfaceElevated,
};

const addBtn: React.CSSProperties = {
  ...T.btnPrimary,
  flexShrink: 0,
  minWidth: 88,
  padding: '0 16px',
};

type Props = {
  tripId: string;
  kind: ChecklistKind;
  templateSectionTitle: string;
  templateDescription: string;
  emptyMessage: string;
  addPlaceholder: string;
  sectionLabels: { open: string; done: string };
};

function InstanceBlock({
  tripId,
  inst,
  sectionLabels,
  addPlaceholder,
}: {
  tripId: string;
  inst: ChecklistInstance;
  sectionLabels: { open: string; done: string };
  addPlaceholder: string;
}) {
  const [draft, setDraft] = useState('');
  const { data: items = [] } = useChecklistItems(inst.id);
  const toggleM = useTogglePacked(inst.id);
  const reorderM = useReorderItems(inst.id, tripId);
  const addM = useAddChecklistItem(inst.id);
  const removeM = useRemoveChecklistItem(inst.id, tripId);

  const submitAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t || addM.isPending) return;
    addM.mutate(t, {
      onSuccess: () => setDraft(''),
    });
  };

  return (
    <section style={T.packingCard}>
      <h3 style={{ ...T.h2, marginBottom: 14, fontSize: '1.1rem' }}>{inst.name}</h3>

      <form onSubmit={submitAdd} style={addRow}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={addPlaceholder}
          style={addInput}
          aria-label="New item label"
        />
        <button
          type="submit"
          className="sf-btn-primary"
          disabled={!draft.trim() || addM.isPending}
          style={{
            ...addBtn,
            opacity: !draft.trim() || addM.isPending ? 0.45 : 1,
            cursor: !draft.trim() || addM.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {addM.isPending ? '…' : 'Add'}
        </button>
      </form>

      {items.length === 0 ? (
        <p style={{ ...T.bodyMuted, margin: 0 }}>No lines yet — type above and tap Add.</p>
      ) : (
        <SortableChecklist
          items={items}
          onToggle={(id) => toggleM.mutate(id)}
          onReorder={(ids) => reorderM.mutate(ids)}
          onRemove={(id) => removeM.mutate(id)}
          removeDisabled={removeM.isPending}
          sectionLabels={sectionLabels}
        />
      )}
    </section>
  );
}

export function TripChecklistTab({
  tripId,
  kind,
  templateSectionTitle,
  templateDescription,
  emptyMessage,
  addPlaceholder,
  sectionLabels,
}: Props) {
  const { data: instances = [], isLoading } = useInstances(tripId, kind);
  const { data: templates = [] } = useTemplates(kind);
  const addFromTemplate = useAddChecklistFromTemplate();
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');

  React.useEffect(() => {
    if (!templates.length) {
      setTemplateId('');
      return;
    }
    if (!templateId || !templates.some((t) => t.id === templateId)) {
      setTemplateId(templates[0].id);
    }
  }, [templates, templateId]);

  const tplBtn: React.CSSProperties = {
    ...T.btnSecondary,
    minWidth: 140,
    opacity: !templateId || addFromTemplate.isPending ? 0.5 : 1,
    cursor: !templateId || addFromTemplate.isPending ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.15s ease',
  };

  const selectStyle: React.CSSProperties = {
    ...T.select,
    flex: 1,
    border: `1px solid ${sf.border}`,
    backgroundColor: sf.surfaceElevated,
  };

  return (
    <div style={T.flexGap}>
      <div style={templateCard}>
        <p style={{ ...T.labelSmall, marginBottom: 8 }}>{templateSectionTitle}</p>
        <p style={{ ...T.bodyMuted, marginTop: 0, marginBottom: 16 }}>{templateDescription}</p>
        <div style={{ ...T.flexRowGap, alignItems: 'stretch' }}>
          <select
            value={templates.length ? templateId : ''}
            onChange={(e) => setTemplateId(e.target.value)}
            style={selectStyle}
            disabled={!templates.length}
            aria-label="Choose template"
          >
            {!templates.length ? (
              <option value="">No templates yet — create one under Templates</option>
            ) : (
              templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))
            )}
          </select>
          <button
            type="button"
            className="sf-btn-secondary"
            disabled={!templateId || !templates.length || addFromTemplate.isPending}
            onClick={() =>
              templateId &&
              addFromTemplate.mutateAsync({ tripId, templateId }).catch(() => {})
            }
            style={tplBtn}
          >
            {addFromTemplate.isPending ? 'Adding…' : 'Add to trip'}
          </button>
        </div>
      </div>

      {isLoading && <p style={T.bodyMuted}>Loading…</p>}
      {!isLoading && instances.length === 0 && <p style={emptyState}>{emptyMessage}</p>}

      <div style={{ ...T.flexGap, gap: 14 }}>
        {instances.map((inst) => (
          <InstanceBlock
            key={inst.id}
            tripId={tripId}
            inst={inst}
            sectionLabels={sectionLabels}
            addPlaceholder={addPlaceholder}
          />
        ))}
      </div>
    </div>
  );
}
